// src/views/admin/WorkOrderDeepDiveDashboard.js
// FlowSense MOW+ — Work Order Deep Dive Analytics Dashboard
// v3.3 | Production-hardened | Merged (v3.2 base + v3.3 delta) | Advanced Loading Diagnostics
//
// DIAGNOSTIC GUIDE — filter browser console by [WO-LOADER]
// ─────────────────────────────────────────────────────────────────────────────
// Healthy initial load (ONE sequence):
//   [WO-LOADER] MOUNT effect fired
//   [WO-LOADER] fetchData ENTERED  fetchingRef=false
//   [WO-LOADER] New AbortController created  cid=1
//   [WO-LOADER] Cache MISS ...
//   [WO-LOADER] Promise.all resolved  cid=1
//   [WO-LOADER] State written to React  cid=1
//   [WO-LOADER] finally  cid=1  willCallSetLoading=true
//   [WO-LOADER] loading state → false
//
// STUCK LOADING pattern (StrictMode double-mount race):
//   [WO-LOADER] MOUNT effect fired           ← mount 1
//   [WO-LOADER] fetchData ENTERED fetchingRef=false  ← mount 1 fetch starts
//   [WO-LOADER] MOUNT CLEANUP fired fetchingRef=true ← abort fires
//   [WO-LOADER] MOUNT effect fired           ← mount 2
//   [WO-LOADER] ⛔ fetchData BLOCKED fetchingRef=true ← root cause: mount 2 skipped
//   [WO-LOADER] finally  aborted=true  willCallSetLoading=false ← no setLoading(false)
//   [WO-LOADER] ⛔ Controller aborted — setLoading(false) WILL NOT be called
//   (spinner stays until user changes a date, which recreates fetchData)
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box, SimpleGrid, Stat, StatLabel, StatNumber, StatHelpText, StatArrow,
  Spinner, Alert, AlertIcon, Heading, VStack, HStack, Button, ButtonGroup,
  IconButton, Tooltip, useToast, Badge, Progress, Flex, Text,
  useColorModeValue, useDisclosure,
  Tabs, TabList, TabPanels, Tab, TabPanel, Select, Switch, FormControl,
  FormLabel, Code, Divider, Menu, MenuButton, MenuList, MenuItem,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter,
  ModalBody, ModalCloseButton, List, ListItem, ListIcon,
  Skeleton, SkeletonText, Icon, Table, Thead, Tbody, Tr, Th, Td,
  TableContainer, InputGroup, InputLeftElement, Input,
  Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon,
  Center
} from '@chakra-ui/react';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer,
  ComposedChart, PieChart, Pie, Cell
} from 'recharts';
import {
  MdRefresh, MdDownload, MdSettings, MdBugReport,
  MdNotifications, MdWarning, MdError, MdCheckCircle,
  MdDashboard, MdSpeed, MdFilterList, MdSearch,
  MdPsychology, MdLightbulb, MdSmartToy, MdModelTraining, MdGrading
} from 'react-icons/md';
import {
  FaDatabase, FaExclamationTriangle, FaCheckCircle, FaTimesCircle
} from 'react-icons/fa';

// ── Services ─────────────────────────────────────────────────────────────────
import analyticsService      from '../../services/analyticsService';
import aiService             from '../../services/aiService';
import { authenticatedFetch } from '../../services/api';
import webSocketService from '../../services/websocketService';

// ── AI Insights Context ───────────────────────────────────────────────────────
import {
  useGlobalAIInsights,
  useOperationalHealth,
  useLiveEventFeed,
  useAIFeatureFlags,
} from '../../contexts/AIInsightsContext';

// ── Child components ──────────────────────────────────────────────────────────
import WorkOrderByPriorityChart  from './WorkOrderByPriorityChart';
import WorkOrderStatsWidget      from './WorkOrderStatsWidget';
import NlpPartPredictorWidget    from './NlpPartPredictorWidget';
import WorkOrderFlowDiagram      from './WorkOrderFlowDiagram';
import AIReviewQueueModal        from './AIReviewQueueModal';
import HumanVerificationWidget   from './HumanVerificationWidget';
import WorkOrderLiveMetrics      from './WorkOrderLiveMetrics';
import WorkOrderBacklogAging from './WorkOrderBacklogAging';
import WorkOrderAssetTypeChart from './WorkOrderAssetTypeChart';
import TechnicianPerformanceWidget from './TechnicianPerformanceWidget';

// =============================================================================
// HOISTED STATIC CONSTANTS
// =============================================================================

const DEFAULT_REFRESH_INTERVAL = 30_000;
const DEFAULT_CACHE_TTL        = 300_000; // 5 minutes

const PERFORMANCE_THRESHOLDS = {
  HIGH_FIX_RATE:           0.8,
  LOW_FIX_RATE:            0.6,
  GOOD_RESOLUTION_HOURS:  24,
  POOR_RESOLUTION_HOURS:  48,
};

const REFRESH_INTERVAL_OPTIONS = [
  { value:  10_000, label: '10'  },
  { value:  30_000, label: '30'  },
  { value:  60_000, label: '60'  },
  { value: 300_000, label: '300' },
];
// =============================================================================
// LOADING DIAGNOSTICS HELPER
// Always-on console logging — fires regardless of debugMode so the browser
// DevTools always shows them. Filter by [WO-LOADER] to read only these.
// Zero impact on production behaviour: purely additive console calls.
// =============================================================================

const DIAG = {
  _ok:   'color:#16a34a;font-weight:bold',
  _bad:  'color:#dc2626;font-weight:bold',
  _info: 'color:#2563eb;font-weight:bold',
  _warn: 'color:#d97706;font-weight:bold',
  _mute: 'color:#6b7280',

  render(count, startStr, endStr) {
    console.log('%c[WO-LOADER] render #%d  start=%s  end=%s', this._mute, count, startStr, endStr);
  },
  mount(count) {
    console.log('%c[WO-LOADER] MOUNT effect fired — renderCount=%d', this._info, count);
  },
  mountCleanup(count, fetchingRef, hasCtrl) {
    console.log(
      '%c[WO-LOADER] MOUNT CLEANUP fired (unmount/StrictMode re-mount) — renderCount=%d  fetchingRef=%s  hasController=%s',
      this._warn, count, fetchingRef, hasCtrl,
    );
  },
  datesChanged(startStr, endStr, count) {
    console.log('%c[WO-LOADER] startStr/endStr CHANGED — start=%s  end=%s  renderCount=%d', this._info, startStr, endStr, count);
  },
  fetchDataRecreated(count) {
    console.log('%c[WO-LOADER] fetchData useCallback RECREATED — renderCount=%d', this._warn, count);
  },
  loadingChanged(loading, count, hasStats) {
    console.log(
      '%c[WO-LOADER] loading state → %s   hasStats=%s   renderCount=%d',
      loading ? this._warn : this._ok, loading, hasStats, count,
    );
  },
  fetchEntry(nextCid, fetchingRef, useCache, count, mounted) {
    console.log(
      '%c[WO-LOADER] fetchData ENTERED  nextCid=%d  fetchingRef=%s  useCache=%s  mounted=%s  renderCount=%d',
      this._info, nextCid, fetchingRef, useCache, mounted, count,
    );
  },
  fetchBlocked(count, loading, hasStats) {
    console.log(
      '%c[WO-LOADER] ⛔ fetchData BLOCKED by fetchingRef=true — PAGE MAY BE STUCK  renderCount=%d  loading=%s  hasStats=%s',
      'color:#dc2626;font-weight:bold;font-size:13px', count, loading, hasStats,
    );
  },
  newController(cid, count) {
    console.log('%c[WO-LOADER] New AbortController created  cid=%d  renderCount=%d', this._info, cid, count);
  },
  abortedPrev(count) {
    console.log('%c[WO-LOADER] Previous controller aborted  renderCount=%d', this._warn, count);
  },
  cacheHit(key, count) {
    console.log('%c[WO-LOADER] Cache HIT — %s  renderCount=%d', this._info, key, count);
  },
  cacheMiss(key, count) {
    console.log('%c[WO-LOADER] Cache MISS — %s  renderCount=%d', this._warn, key, count);
  },
  promiseAllResolved(cid, count) {
    console.log('%c[WO-LOADER] Promise.all resolved  cid=%d  renderCount=%d', this._info, cid, count);
  },
  abortedAfterResolve(cid, count) {
    console.log('%c[WO-LOADER] ⛔ Aborted AFTER Promise.all — discarding results  cid=%d  renderCount=%d', this._bad, cid, count);
  },
  stateWritten(cid, count) {
    console.log('%c[WO-LOADER] React state written (mounted path)  cid=%d  renderCount=%d', this._ok, cid, count);
  },
  fetchError(cid, errName, count) {
    console.log('%c[WO-LOADER] fetchData caught error  cid=%d  err=%s  renderCount=%d', this._bad, cid, errName, count);
  },
  finallyBlock(cid, match, aborted, mounted, willSet, count) {
    const style = willSet ? this._ok : this._bad;
    console.log(
      '%c[WO-LOADER] finally  cid=%d  controllerMatch=%s  aborted=%s  mounted=%s  willCallSetLoading=%s  renderCount=%d',
      style, cid, match, aborted, mounted, willSet, count,
    );
  },
  refreshKeyTriggered(key, count) {
    console.log('%c[WO-LOADER] refreshKey trigger  key=%d  renderCount=%d', this._info, key, count);
  },
};



// =============================================================================
// DEBUG LOGGER
// =============================================================================

class DebugLogger {
  constructor(enabled = false) {
    this.enabled = enabled;
    this.logs    = [];
    this.maxLogs = 1000;
    this.performanceMarks = new Map();
  }

  log(level, message, data = null) {
    if (!this.enabled) return;
    const timestamp = new Date().toISOString();
    const logEntry  = { timestamp, level, message, data };
    this.logs.push(logEntry);
    if (this.logs.length > this.maxLogs) this.logs.shift();
    (console[level] || console.log)(
      `[${timestamp}] [${level.toUpperCase()}] ${message}`, data || ''
    );
  }

  info(m, d)  { this.log('info',  m, d); }
  warn(m, d)  { this.log('warn',  m, d); }
  error(m, d) { this.log('error', m, d); }
  debug(m, d) { this.log('debug', m, d); }

  startPerformance(label) {
    if (!this.enabled) return;
    this.performanceMarks.set(label, performance.now());
  }

  endPerformance(label) {
    if (!this.enabled) return 0;
    const start = this.performanceMarks.get(label);
    if (!start) return 0;
    const dur = performance.now() - start;
    this.performanceMarks.delete(label);
    return dur;
  }

  getLogs(level = null) {
    return level ? this.logs.filter(l => l.level === level) : this.logs;
  }
  clearLogs()  { this.logs = []; this.performanceMarks.clear(); }
  exportLogs() { return JSON.stringify(this.logs, null, 2); }
}

// =============================================================================
// DATA CACHE
// =============================================================================

class DataCache {
  constructor(ttl = DEFAULT_CACHE_TTL) { this.cache = new Map(); this.ttl = ttl; }

  set(key, value) { this.cache.set(key, { value, timestamp: Date.now() }); }

  get(key) {
    const item = this.cache.get(key);
    if (!item) return null;
    if (Date.now() - item.timestamp > this.ttl) { this.cache.delete(key); return null; }
    return item.value;
  }

  clear() { this.cache.clear(); }

  has(key) {
    const item = this.cache.get(key);
    if (!item) return false;
    if (Date.now() - item.timestamp > this.ttl) { this.cache.delete(key); return false; }
    return true;
  }

  size() { return this.cache.size; }
}

// =============================================================================
// ERROR BOUNDARY
// =============================================================================

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) { return { hasError: true }; }

  componentDidCatch(error, errorInfo) {
    this.setState({ error, errorInfo });
    if (this.props.logger) {
      this.props.logger.error('ErrorBoundary caught', {
        error: error.toString(), errorInfo,
      });
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <Alert
          status="error"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          height="200px"
        >
          <AlertIcon boxSize="40px" mr={0} />
          <Heading size="md" mt={4} mb={2}>Something went wrong</Heading>
          <Text>{this.state.error && this.state.error.toString()}</Text>
          <Button
            mt={4}
            onClick={() => this.setState({ hasError: false, error: null })}
          >
            Try again
          </Button>
        </Alert>
      );
    }
    return this.props.children;
  }
}

// =============================================================================
// MEMOIZED TECHNICIAN ROW
// =============================================================================

const TechnicianRow = React.memo(function TechnicianRow({
  tech, index, textColor, tableRowHoverBg,
}) {
  const fixRate        = parseFloat(tech.first_time_fix_rate);
  const resolutionTime = parseFloat(tech.avg_resolution_time_hours);

  const isHighPerformer  =
    fixRate > PERFORMANCE_THRESHOLDS.HIGH_FIX_RATE &&
    resolutionTime < PERFORMANCE_THRESHOLDS.GOOD_RESOLUTION_HOURS;

  const needsImprovement =
    fixRate < PERFORMANCE_THRESHOLDS.LOW_FIX_RATE ||
    resolutionTime > PERFORMANCE_THRESHOLDS.POOR_RESOLUTION_HOURS;

  const resolutionColor =
    resolutionTime < PERFORMANCE_THRESHOLDS.GOOD_RESOLUTION_HOURS ? 'green.500' :
    resolutionTime < PERFORMANCE_THRESHOLDS.POOR_RESOLUTION_HOURS ? 'yellow.500' : 'red.500';

  const fixRateColor =
    fixRate > PERFORMANCE_THRESHOLDS.HIGH_FIX_RATE ? 'green.500' :
    fixRate > PERFORMANCE_THRESHOLDS.LOW_FIX_RATE  ? 'yellow.500' : 'red.500';

  return (
    <Tr _hover={{ bg: tableRowHoverBg }}>
      <Td fontWeight="medium" color={textColor}>{tech.technician_name}</Td>
      <Td isNumeric><Badge colorScheme="blue">{tech.completed_work_orders}</Badge></Td>
      <Td isNumeric>
        <Tooltip label={`${resolutionTime.toFixed(2)} hours`}>
          <Text color={resolutionColor} fontWeight="semibold">{resolutionTime.toFixed(1)}</Text>
        </Tooltip>
      </Td>
      <Td isNumeric>
        <Tooltip label={`${(fixRate * 100).toFixed(1)}%`}>
          <Text color={fixRateColor} fontWeight="semibold">{(fixRate * 100).toFixed(1)}%</Text>
        </Tooltip>
      </Td>
      <Td>
        {isHighPerformer  && <Badge colorScheme="green">Excellent</Badge>}
        {needsImprovement && <Badge colorScheme="red">Needs Training</Badge>}
        {!isHighPerformer && !needsImprovement && <Badge colorScheme="blue">Good</Badge>}
      </Td>
    </Tr>
  );
});

// =============================================================================
// MAIN COMPONENT
// =============================================================================

// --- UPDATE: Added refreshKey prop ---
function WorkOrderDeepDiveDashboard({ dateRange = {}, refreshKey = 0 }) {
  const { start: startDate, end: endDate } = dateRange;

  // --- FIX 1: Stabilize fallback dates with useMemo so that new Date() is only
  // called once on mount rather than on every render. Previously, the inline
  // `new Date()` calls ran on every render, producing a new toISOString() value
  // every millisecond when dateRange was empty. That caused startStr/endStr to
  // change on every render -> fetchData useCallback was recreated -> the initial
  // fetch useEffect fired again -> infinite GET /work-order-stats loop. ---
  const fallbackDates = useMemo(() => {
    const fallbackEnd   = new Date();
    const fallbackStart = new Date();
    fallbackStart.setDate(fallbackEnd.getDate() - 30);
    return { start: fallbackStart, end: fallbackEnd };
  }, []);

  const resolvedStart = startDate || fallbackDates.start;
  const resolvedEnd   = endDate   || fallbackDates.end;
  // --- END FIX 1 ---

  // --- MERGE FROM v3.3: Wrap startStr and endStr in useMemo for an additional
  // layer of stability. Prevents the string from being recalculated on every
  // render in case resolvedStart/End receives an equivalent-but-new object. ---
  const startStr = useMemo(
    () =>
      resolvedStart instanceof Date
        ? resolvedStart.toISOString()
        : new Date(resolvedStart).toISOString(),
    [resolvedStart],
  );

  const endStr = useMemo(
    () =>
      resolvedEnd instanceof Date
        ? resolvedEnd.toISOString()
        : new Date(resolvedEnd).toISOString(),
    [resolvedEnd],
  );
  // --- END MERGE FROM v3.3 ---

  // ── AIInsightsContext ─────────────────────────────────────────────────────
  const { insights: contextInsights, isConnected: wsIsConnected } = useGlobalAIInsights();
  const { watchdogHealth: contextWatchdog }                        = useOperationalHealth();
  const liveEventFeed                                              = useLiveEventFeed();
  const featureFlags                                               = useAIFeatureFlags();

  // ── Theme tokens ──────────────────────────────────────────────────────────
  const bgColor          = useColorModeValue('gray.50',   'gray.900');
  const cardBg           = useColorModeValue('white',     'gray.800');
  const borderColor      = useColorModeValue('gray.200',  'gray.600');
  const textColor        = useColorModeValue('gray.700',  'gray.200');
  const headingColor     = useColorModeValue('gray.800',  'white');
  const mutedColor       = useColorModeValue('gray.600',  'gray.400');
  const statBg           = useColorModeValue('gray.50',   'gray.700');
  const iconBg           = useColorModeValue('blue.50',   'blue.900');
  const aiHealthBg       = useColorModeValue('purple.50', 'purple.900');
  const tableRowHoverBg  = useColorModeValue('gray.50',   'gray.700');
  const headerBg         = useColorModeValue('white',     'gray.800');
  const liveTickerBg     = useColorModeValue('gray.50',   'gray.750');
  const liveTickerBorder = useColorModeValue('gray.200',  'gray.600');

  // ── Operational data state ────────────────────────────────────────────────
  const [funnelData,      setFunnelData]      = useState([]);
  const [backlogData,     setBacklogData]      = useState([]);
  const [byTypeData,      setByTypeData]       = useState([]);
  const [byAssetTypeData, setByAssetTypeData]  = useState([]);
  const [loading,         setLoading]          = useState(true);
  const [error,           setError]            = useState(null);
  const [stats,           setStats]            = useState(null);
  const [techPerformance, setTechPerformance]  = useState([]);
  const [sortConfig,      setSortConfig]       = useState({ key: null, direction: 'asc' });
  const [searchTerm,      setSearchTerm]       = useState('');
  const [selectedMetric,  setSelectedMetric]   = useState('all');

  // ── AI system state ───────────────────────────────────────────────────────
  const [aiHealth,          setAiHealth]          = useState(null);
  const [aiModels,          setAiModels]          = useState([]);
  const [aiStats,           setAiStats]           = useState(null);
  const [aiInsights,        setAiInsights]        = useState([]);
  const [isAiHealthy,       setIsAiHealthy]       = useState(true);
  const [reviewQueue,       setReviewQueue]       = useState([]);
  const [verifiedCount,     setVerifiedCount]     = useState(0);
  const [isTraining,        setIsTraining]        = useState(false);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);

  // ── Advanced features state ───────────────────────────────────────────────
  const [debugMode,          setDebugMode]          = useState(false);
  const [autoRefresh,        setAutoRefresh]        = useState(false);
  const [refreshInterval,    setRefreshInterval]    = useState(DEFAULT_REFRESH_INTERVAL);
  const [performanceMetrics, setPerformanceMetrics] = useState({});
  const [cacheHitRate,       setCacheHitRate]       = useState(0);
  const [apiCallCount,       setApiCallCount]       = useState(0);
  const [dataFreshness,      setDataFreshness]      = useState(null);
  const [connectionStatus,   setConnectionStatus]   = useState('connecting');
  const [advancedFilters,    setAdvancedFilters]    = useState({
    minCompletedWOs:    0,
    maxResolutionTime:  Infinity,
    minFixRate:         0,
  });
  const [activeTab, setActiveTab] = useState(0);

  // --- MERGE FROM v3.3: Inline advanced filter toggle for the Technician
  // Performance card. The existing advancedFilters state is shared with the
  // Settings modal so both surfaces stay in sync automatically. This state only
  // controls whether the inline panel is visible. ---
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  // --- END MERGE FROM v3.3 ---

  // ── DIAGNOSTIC REFS ──────────────────────────────────────────────────────
  // renderCountRef: incremented on every render call so each log line carries
  // its render-cycle number. Lets you confirm StrictMode double-mount and see
  // exactly which render each useEffect fires on.
  const renderCountRef = useRef(0);
  // controllerIdRef: monotonically-increasing ID assigned to each new
  // AbortController. Lets you correlate a finally-block log to the correct
  // fetchData invocation even when multiple controllers are created.
  const controllerIdRef = useRef(0);
  // ── END DIAGNOSTIC REFS ──────────────────────────────────────────────────

  // ── Refs ──────────────────────────────────────────────────────────────────
  const debugLogger = useRef(null);
  if (debugLogger.current === null) debugLogger.current = new DebugLogger(debugMode);

  const dataCache = useRef(null);
  if (dataCache.current === null) dataCache.current = new DataCache();

  const refreshTimerRef = useRef(null);
  const mountedRef      = useRef(true);

  // Concurrent-fetch guard
  const fetchingRef = useRef(false);

  // Cancels in-flight requests on new call / unmount
  const abortControllerRef = useRef(null);

  // Stale-closure-safe read of connectionStatus inside WS handlers
  const connectionStatusRef = useRef('connecting');

  // ── UI hooks ──────────────────────────────────────────────────────────────
  const toast = useToast();
  const { isOpen: isDebugOpen,    onOpen: onDebugOpen,    onClose: onDebugClose    } = useDisclosure();
  const { isOpen: isSettingsOpen, onOpen: onSettingsOpen, onClose: onSettingsClose } = useDisclosure();

  // ── DIAGNOSTIC: Increment render counter on every render ─────────────────
  // Plain assignment in the function body — runs synchronously on every
  // render invocation, giving each hook/callback/effect a render-cycle number.
  renderCountRef.current += 1;

  // ── DIAGNOSTIC: Log every render with current date strings ───────────────
  // If you see this firing rapidly without user interaction, startStr/endStr
  // are unstable and the fetchData useCallback is being recreated in a loop.
  DIAG.render(renderCountRef.current, startStr, endStr);
  // ── END DIAGNOSTIC ────────────────────────────────────────────────────────

  // ── Debug mode sync ───────────────────────────────────────────────────────
  useEffect(() => {
    debugLogger.current.enabled = debugMode;
    if (debugMode) debugLogger.current.info('Debug mode enabled');
    // DEBUG: log when debug mode is explicitly turned off
    if (!debugMode) debugLogger.current.info('Debug mode disabled');
  }, [debugMode]);

  // ── DIAGNOSTIC: Track loading state changes ───────────────────────────────
  // A final log of loading→true with no subsequent loading→false confirms that
  // setLoading(false) was never reached — the stuck-loading bug is live.
  useEffect(() => {
    DIAG.loadingChanged(loading, renderCountRef.current, !!stats);
    debugLogger.current.debug('DIAG: loading state changed', {
      loading,
      renderCount: renderCountRef.current,
      hasStats:    !!stats,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  // ── DIAGNOSTIC: Track startStr/endStr stability ───────────────────────────
  // Fires when either date string changes. More than one fire without user
  // interaction means the fallback-date memoisation is broken and the dates
  // are cycling — that drives the original infinite-fetch loop.
  useEffect(() => {
    DIAG.datesChanged(startStr, endStr, renderCountRef.current);
    debugLogger.current.debug('DIAG: startStr/endStr changed', {
      startStr,
      endStr,
      renderCount: renderCountRef.current,
    });
  }, [startStr, endStr]);



  // ── Mount / unmount ───────────────────────────────────────────────────────
  useEffect(() => {
    // DIAGNOSTIC: Log mount. In StrictMode you will see MOUNT / CLEANUP / MOUNT
    // in sequence. The second MOUNT is where the real fetchData call happens.
    DIAG.mount(renderCountRef.current);
    debugLogger.current.info('WorkOrderDeepDiveDashboard mounted', {
      renderCount: renderCountRef.current,
    });
    mountedRef.current = true;

    return () => {
      // DIAGNOSTIC: Log cleanup. If fetchingRef is true here AND the async
      // fetch has not completed, the abort() below will cause setLoading(false)
      // to be skipped in the finally block — the classic stuck-loading scenario.
      DIAG.mountCleanup(renderCountRef.current, fetchingRef.current, !!abortControllerRef.current);
      debugLogger.current.info('WorkOrderDeepDiveDashboard unmounting', {
        renderCount:          renderCountRef.current,
        fetchingRefAtCleanup: fetchingRef.current,
        hasController:        !!abortControllerRef.current,
        currentControllerId:  controllerIdRef.current,
      });
      mountedRef.current = false;
      // --- FIX: Reset fetchingRef BEFORE aborting the controller.
      //
      // Without this, the StrictMode double-mount race permanently blocks the
      // initial data load:
      //   1. Mount 1 starts a fetch, fetchingRef = true.
      //   2. StrictMode cleanup aborts the controller but leaves fetchingRef = true.
      //   3. Mount 2's useEffect([fetchData]) calls fetchData() — hits the
      //      fetchingRef guard and returns early (BLOCKED).
      //   4. Mount 1's fetch eventually resolves but sees controller.signal.aborted
      //      = true, so skips setLoading(false). Page stays on the spinner forever.
      //
      // With this fix:
      //   2b. Cleanup sets fetchingRef = false before aborting.
      //   3b. Mount 2's fetchData proceeds: creates cid=2, starts a fresh fetch.
      //   4b. Mount 1's finally sees abortControllerRef.current === cid=2 ≠ cid=1,
      //       so controllerMatch = false — correctly does nothing.
      //   5b. cid=2 completes normally, calls setLoading(false) with real data. ✓
      fetchingRef.current = false;
      // --- END FIX ---
      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
      if (abortControllerRef.current) abortControllerRef.current.abort();
      connectionStatusRef.current = 'disconnected';
    };
  }, []);

  // ── DATA FETCHING ─────────────────────────────────────────────────────────

  const fetchData = useCallback(async (useCache = true) => {
    // ── DIAGNOSTIC: Log every entry ───────────────────────────────────────
    DIAG.fetchEntry(controllerIdRef.current + 1, fetchingRef.current, useCache, renderCountRef.current, mountedRef.current);
    debugLogger.current.debug('DIAG: fetchData entered', {
      nextControllerId:   controllerIdRef.current + 1,
      fetchingRefCurrent: fetchingRef.current,
      useCache,
      renderCount:        renderCountRef.current,
      mountedRef:         mountedRef.current,
    });
    // ── END DIAGNOSTIC ────────────────────────────────────────────────────

    // Prevent fetching if one is already in flight
    if (fetchingRef.current) {
      // ── DIAGNOSTIC: This early-exit is THE stuck-loading trigger. ─────────
      // If this fires on the initial page load, loading stays true until
      // startStr/endStr change (user picks a date), which recreates fetchData.
      DIAG.fetchBlocked(renderCountRef.current, loading, !!stats);
      debugLogger.current.warn('DIAG: fetchData early-exit — fetchingRef.current is true', {
        renderCount: renderCountRef.current, loading, hasStats: !!stats,
      });
      // ── END DIAGNOSTIC ──────────────────────────────────────────────────
      debugLogger.current.debug('fetchData skipped — already in-flight');
      return;
    }

    // Cancel any previous in-flight request
    if (abortControllerRef.current) {
      DIAG.abortedPrev(renderCountRef.current);
      debugLogger.current.debug('DIAG: aborting previous controller', {
        previousControllerId: controllerIdRef.current, renderCount: renderCountRef.current,
      });
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    // ── DIAGNOSTIC: Assign unique ID to this controller ───────────────────
    controllerIdRef.current += 1;
    const controllerId = controllerIdRef.current;
    DIAG.newController(controllerId, renderCountRef.current);
    debugLogger.current.debug('DIAG: new AbortController assigned', { controllerId, renderCount: renderCountRef.current });
    // ── END DIAGNOSTIC ────────────────────────────────────────────────────

    fetchingRef.current = true;
    debugLogger.current.startPerformance('fetchData');

    try {
      setLoading(true);
      setError(null);

      // Use primitive strings for cache key stability
      const cacheKey = `workorder-data-${startStr}-${endStr}`;

      if (useCache && dataCache.current.has(cacheKey)) {
        DIAG.cacheHit(cacheKey, renderCountRef.current);
        debugLogger.current.info('Cache hit', { cacheKey, controllerId });
        const cached = dataCache.current.get(cacheKey);
        setFunnelData(cached.funnelData);
        setBacklogData(cached.backlogData);
        setByTypeData(cached.byTypeData);
        setByAssetTypeData(cached.byAssetTypeData);
        setStats(cached.stats);
        setTechPerformance(cached.techPerformance);
        setDataFreshness(cached.timestamp);
        if (cached.aiHealth)    setAiHealth(cached.aiHealth);
        if (cached.aiModels)    setAiModels(cached.aiModels);
        if (cached.aiStats)     setAiStats(cached.aiStats);
        if (cached.aiInsights)  setAiInsights(cached.aiInsights);
        if (cached.reviewQueue) setReviewQueue(cached.reviewQueue);
        setCacheHitRate(prev => prev + 1);
        // DEBUG: log counts of restored cache fields for inspection
        debugLogger.current.debug('Cache restored — state counts', {
          funnelData:      cached.funnelData?.length,
          backlogData:     cached.backlogData?.length,
          byTypeData:      cached.byTypeData?.length,
          byAssetTypeData: cached.byAssetTypeData?.length,
          techPerformance: cached.techPerformance?.length,
          aiModels:        cached.aiModels?.length,
          aiInsights:      cached.aiInsights?.length,
          reviewQueue:     cached.reviewQueue?.length,
          cachedAt:        cached.timestamp,
        });
        setLoading(false);
        return;
      }

      DIAG.cacheMiss(cacheKey, renderCountRef.current);
      debugLogger.current.info('Cache miss — fetching from API', { cacheKey, controllerId });
      setApiCallCount(prev => prev + 1);

      const params = { startDate: startStr, endDate: endStr };

      // DEBUG: log the params being sent to all API calls
      debugLogger.current.debug('fetchData — dispatching Promise.all', { params });

      const [
        funnelRes, backlogRes, byTypeRes, byAssetTypeRes,
        statsRes, techRes,
        aiHealthRes, aiModelsRes, aiInsightsRes, reviewQueueRes,
      ] = await Promise.all([
        analyticsService.getWidgetData('work-order-funnel',         params).catch(e => { debugLogger.current.error('funnel',    e); return { data: { data: [] } }; }),
        analyticsService.getWidgetData('backlog-aging',             params).catch(e => { debugLogger.current.error('backlog',   e); return { data: { data: [] } }; }),
        analyticsService.getWidgetData('work-orders-by-type',       params).catch(e => { debugLogger.current.error('byType',   e); return { data: { data: [] } }; }),
        analyticsService.getWidgetData('work-orders-by-asset-type', params).catch(e => { debugLogger.current.error('byAsset',  e); return { data: { data: [] } }; }),
        analyticsService.fetchWorkOrderStats().catch(e => { debugLogger.current.error('stats', e); return { data: {} }; }),
        analyticsService.fetchTechnicianPerformance().catch(e => { debugLogger.current.error('tech', e); return { data: [] }; }),
        aiService.getAIHealthMetrics().catch(e => { debugLogger.current.warn('aiHealth', e); return { status: 'unknown', models_active: 0, last_check: null }; }),
        aiService.getAvailableModels().catch(e => { debugLogger.current.warn('aiModels', e); return { models: [] }; }),
        authenticatedFetch('/ai/global-insights').catch(e => { debugLogger.current.warn('aiInsights', e); return []; }),
        aiService.getReviewQueue().catch(e => { debugLogger.current.warn('reviewQueue', e); return []; }),
      ]);

      // DEBUG: confirm all promises resolved before processing
      DIAG.promiseAllResolved(controllerId, renderCountRef.current);
      debugLogger.current.debug('fetchData — Promise.all resolved, processing results', { controllerId });

      if (controller.signal.aborted) {
        // DEBUG: explicit log when an abort is detected post-await
        DIAG.abortedAfterResolve(controllerId, renderCountRef.current);
        debugLogger.current.warn('fetchData — aborted after Promise.all resolved, discarding results', {
          controllerId, renderCount: renderCountRef.current,
        });
        return;
      }

      const aiModelsArr   = aiModelsRes?.models || (Array.isArray(aiModelsRes) ? aiModelsRes : []);
      const aiInsightsArr = Array.isArray(aiInsightsRes)
        ? aiInsightsRes
        : (aiInsightsRes?.insights || aiInsightsRes?.data || []);
      const reviewQueueArr = Array.isArray(reviewQueueRes) ? reviewQueueRes : [];

      const prodModels    = aiModelsArr.filter(m => m.status === 'production');
      const stagingModels = aiModelsArr.filter(m => m.status === 'staging');

      const calculatedAiStats = {
        activeModels:  prodModels.length,
        stagingModels: stagingModels.length,
        totalModels:   aiModelsArr.length,
        lastTraining:  prodModels[0]?.last_trained_at || null,
        systemStatus:  aiHealthRes?.status || 'unknown',
      };

      // DEBUG: log the derived AI stats before writing to state
      debugLogger.current.debug('fetchData — aiStats calculated', {
        ...calculatedAiStats,
        prodModelIds:      prodModels.map(m => m.id),
        stagingModelIds:   stagingModels.map(m => m.id),
        rawAiHealthStatus: aiHealthRes?.status,
        insightsCount:     aiInsightsArr.length,
        reviewQueueCount:  reviewQueueArr.length,
      });

      // Helper to extract data safely regardless of API wrapper depth
      const extract = (res) => res?.data?.data || res?.data || res || [];

      const newData = {
        funnelData:      extract(funnelRes),
        backlogData:     extract(backlogRes),
        byTypeData:      extract(byTypeRes),
        byAssetTypeData: extract(byAssetTypeRes),
        stats:           statsRes?.data || statsRes || {},
        techPerformance: techRes?.data?.data || techRes?.data || techRes || [],
        timestamp:       new Date().toISOString(),
        aiHealth:        aiHealthRes,
        aiModels:        aiModelsArr,
        aiStats:         calculatedAiStats,
        aiInsights:      aiInsightsArr,
        reviewQueue:     reviewQueueArr,
        verifiedCount:   reviewQueueRes?.verifiedCount || 0,
      };

      dataCache.current.set(cacheKey, newData);
      debugLogger.current.info('Data cached', { cacheKey });

      if (mountedRef.current) {
        setFunnelData(newData.funnelData);
        setBacklogData(newData.backlogData);
        setByTypeData(newData.byTypeData);
        setByAssetTypeData(newData.byAssetTypeData);
        setStats(newData.stats);
        setTechPerformance(newData.techPerformance);
        setDataFreshness(newData.timestamp);
        setAiHealth(newData.aiHealth);
        setAiModels(newData.aiModels);
        setAiStats(newData.aiStats);
        setAiInsights(newData.aiInsights);
        setReviewQueue(newData.reviewQueue);
        setVerifiedCount(newData.verifiedCount);
        setIsAiHealthy(
          aiHealthRes?.status === 'ok' || aiHealthRes?.status === 'healthy'
        );
        // DEBUG: log final state write counts after a fresh API fetch
        DIAG.stateWritten(controllerId, renderCountRef.current);
        debugLogger.current.info('fetchData — state updated from API', {
          controllerId,
          funnelRows:      newData.funnelData.length,
          backlogRows:     newData.backlogData.length,
          byTypeRows:      newData.byTypeData.length,
          byAssetTypeRows: newData.byAssetTypeData.length,
          techRows:        newData.techPerformance.length,
          aiModels:        newData.aiModels.length,
          aiInsights:      newData.aiInsights.length,
          reviewQueue:     newData.reviewQueue.length,
          verifiedCount:   newData.verifiedCount,
          isAiHealthy:     aiHealthRes?.status === 'ok' || aiHealthRes?.status === 'healthy',
        });
      }
    } catch (err) {
      if (err?.name === 'AbortError' || controller.signal.aborted) return;

      DIAG.fetchError(controllerId, err?.name || 'Error', renderCountRef.current);
      debugLogger.current.error('fetchData error', { error: err.message, controllerId });
      if (mountedRef.current) {
        setError('Failed to fetch work order analytics data.');
        toast({
          title:       'Error Loading Data',
          description: err.message || 'Failed to fetch analytics.',
          status:      'error',
          duration:    5000,
          isClosable:  true,
        });
      }
    } finally {
      // ── DIAGNOSTIC: Log the full finally picture ─────────────────────────
      // willCallSetLoading must be true for loading to clear. If false, the
      // page stays on the spinner until something recreates fetchData.
      const _ctrlMatch  = abortControllerRef.current === controller;
      const _aborted    = controller.signal.aborted;
      const _mounted    = mountedRef.current;
      const _willSet    = _ctrlMatch && _mounted && !_aborted;
      DIAG.finallyBlock(controllerId, _ctrlMatch, _aborted, _mounted, _willSet, renderCountRef.current);
      debugLogger.current.debug('DIAG: finally block', {
        controllerId,
        controllerStillMatches: _ctrlMatch,
        isAborted:              _aborted,
        isMounted:              _mounted,
        willCallSetLoading:     _willSet,
        renderCount:            renderCountRef.current,
      });
      // ── END DIAGNOSTIC ───────────────────────────────────────────────────

      // Release lock regardless of outcome
      if (_ctrlMatch) {
        fetchingRef.current = false;
        if (_mounted && !_aborted) {
          setLoading(false);
          const dur = debugLogger.current.endPerformance('fetchData');
          setPerformanceMetrics(prev => ({
            ...prev,
            lastFetchDuration: dur,
            avgFetchDuration:  prev.avgFetchDuration
              ? (prev.avgFetchDuration + dur) / 2
              : dur,
          }));
          debugLogger.current.debug('fetchData — finally block: setLoading(false) called', {
            controllerId,
            durationMs: dur ? dur.toFixed(2) : 'n/a',
          });
        }
      }
    }
  }, [startStr, endStr, toast]);

  // ── DIAGNOSTIC: Track fetchData callback identity changes ─────────────────
  // MUST be placed AFTER the fetchData useCallback declaration to avoid the
  // Temporal Dead Zone (TDZ) — referencing a const before it is initialised
  // causes "Cannot access 'fetchData' before initialization" at render time.
  // Fires every time the fetchData useCallback identity changes (startStr,
  // endStr, or toast changed). More than one fire on initial load means one
  // of those deps is unstable and is causing a spurious re-fetch loop.
  useEffect(() => {
    DIAG.fetchDataRecreated(renderCountRef.current);
    debugLogger.current.debug('DIAG: fetchData useCallback identity changed', {
      renderCount: renderCountRef.current,
    });
  }, [fetchData]);

  // ── Initial fetch ──────────────────────────────────────────────────────────
  useEffect(() => {
    debugLogger.current.info('Initial mount — fetchData');
    fetchData();
  }, [fetchData]);

  // ── External Refresh Trigger ───────────────────────────────────────────────
  // --- UPDATE: Added useEffect to listen for refreshKey changes ---
  useEffect(() => {
    if (refreshKey > 0) {
      DIAG.refreshKeyTriggered(refreshKey, renderCountRef.current);
      debugLogger.current.info('Manual refresh triggered via refreshKey', {
        refreshKey, renderCount: renderCountRef.current,
      });
      fetchData(false); // false = bypass cache and force a new network request
    }
  }, [refreshKey, fetchData]);
  // --- END UPDATE ---

  // ── Auto-refresh ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (autoRefresh) {
      debugLogger.current.info('Auto-refresh enabled', { interval: refreshInterval });
      refreshTimerRef.current = setInterval(() => {
        debugLogger.current.info('Auto-refresh triggered');
        fetchData(false);
      }, refreshInterval);

      return () => {
        if (refreshTimerRef.current) {
          clearInterval(refreshTimerRef.current);
          // DEBUG: confirm cleanup fires on interval/autoRefresh change
          debugLogger.current.info('Auto-refresh interval cleared on effect cleanup', {
            intervalMs: refreshInterval,
          });
        }
      };
    }

    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current);
      refreshTimerRef.current = null;
      // DEBUG: log when autoRefresh is turned off and the timer is cleared
      debugLogger.current.info('Auto-refresh disabled — timer cleared');
    }
  }, [autoRefresh, refreshInterval, fetchData]);

  // ── WEBSOCKET SUBSCRIPTIONS ────────────────────────────────────────────────

  useEffect(() => {
    debugLogger.current.info('WebSocket: Attaching FlowSense subscriptions');

    const onWatchdogEvent = (payload) => {
      if (!mountedRef.current) return;
      const type = payload?.type;
      debugLogger.current.debug('watchdog_event', { type });

      if (type === 'scan_complete') {
        // DEBUG: log efficiency rating from scan payload
        debugLogger.current.info('watchdog_event: scan_complete — invalidating cache', {
          efficiencyRating: payload?.efficiency_rating ?? 'not provided',
          assetId:          payload?.asset_id ?? null,
        });
        dataCache.current.clear();
        fetchData(false);
        if (payload?.efficiency_rating != null) {
          setIsAiHealthy(payload.efficiency_rating > 70);
          // DEBUG: log the health threshold decision
          debugLogger.current.debug('watchdog_event: isAiHealthy updated from efficiency_rating', {
            efficiency_rating: payload.efficiency_rating,
            threshold:         70,
            result:            payload.efficiency_rating > 70,
          });
        }
      }

      if (type === 'circuit_open') {
        // DEBUG: log full circuit_open payload for post-mortem debugging
        debugLogger.current.warn('watchdog_event: circuit_open — ML predictions paused', {
          reason:    payload?.reason    ?? 'unknown',
          assetId:   payload?.asset_id  ?? null,
          threshold: payload?.threshold ?? null,
          timestamp: payload?.timestamp ?? new Date().toISOString(),
        });
        setIsAiHealthy(false);
        connectionStatusRef.current = 'degraded';
        setConnectionStatus('degraded');
        toast({
          title:       'ML Circuit Breaker Tripped',
          description: 'AI predictions are temporarily paused. Check the ML Engine.',
          status:      'warning',
          duration:    8000,
          isClosable:  true,
          position:    'top-right',
        });
      }

      if (type === 'circuit_closed') {
        // DEBUG: log circuit restore payload
        debugLogger.current.info('watchdog_event: circuit_closed — ML predictions resumed', {
          restoredAt: payload?.timestamp ?? new Date().toISOString(),
          assetId:    payload?.asset_id  ?? null,
        });
        setIsAiHealthy(true);
        connectionStatusRef.current = 'connected';
        setConnectionStatus('connected');
        toast({
          title:       'ML Circuit Restored',
          description: 'AI predictions have resumed normal operation.',
          status:      'success',
          duration:    4000,
          isClosable:  true,
          position:    'top-right',
        });
      }
    };

    const onWorkOrderEvent = (payload) => {
      if (!mountedRef.current) return;
      const hot = [
        'sla_watchdog_update',
        'unassigned_critical',
        'stagnant_flagged',
        'ai_neglect_retriggered',
      ];
      // DEBUG: log whether the incoming event type is hot or ignored
      debugLogger.current.debug('work_order_event received', {
        type:        payload?.type ?? 'unknown',
        isHot:       hot.includes(payload?.type),
        workOrderId: payload?.work_order_id ?? null,
        assetId:     payload?.asset_id      ?? null,
      });
      if (hot.includes(payload?.type)) {
        // DEBUG: log which hot event triggered a cache bust + refetch
        debugLogger.current.info('work_order_event: hot event — invalidating cache and refetching', {
          type: payload?.type,
        });
        dataCache.current.clear();
        fetchData(false);
      }
    };

    const onAssetHealthEvent = (payload) => {
      if (!mountedRef.current) return;
      // DEBUG: log the full health payload on every asset_health_event
      debugLogger.current.debug('asset_health_event received', {
        healthScore:     payload?.health_score     ?? null,
        assetId:         payload?.asset_id         ?? null,
        predictedRUL:    payload?.predicted_rul    ?? null,
        confidenceScore: payload?.confidence_score ?? null,
        previousStatus:  connectionStatusRef.current,
      });
      if (payload?.health_score != null) {
        setIsAiHealthy(true);
        if (connectionStatusRef.current !== 'connected') {
          // DEBUG: log the connection status transition triggered by a health event
          debugLogger.current.info('asset_health_event: connection status transition', {
            from: connectionStatusRef.current,
            to:   'connected',
          });
          connectionStatusRef.current = 'connected';
          setConnectionStatus('connected');
        }
      }
    };

    webSocketService?.subscribe?.('watchdog_events',     onWatchdogEvent);
    webSocketService?.subscribe?.('work_order_events',   onWorkOrderEvent);
    webSocketService?.subscribe?.('asset_health_events', onAssetHealthEvent);

    connectionStatusRef.current = 'connected';
    setConnectionStatus('connected');
    // DEBUG: confirm all three channels were subscribed
    debugLogger.current.info('WebSocket: All FlowSense channels subscribed', {
      channels: ['watchdog_events', 'work_order_events', 'asset_health_events'],
    });

    return () => {
      webSocketService?.unsubscribe?.('watchdog_events',     onWatchdogEvent);
      webSocketService?.unsubscribe?.('work_order_events',   onWorkOrderEvent);
      webSocketService?.unsubscribe?.('asset_health_events', onAssetHealthEvent);
      debugLogger.current.info('WebSocket: All channels unsubscribed');
    };
  // connectionStatus intentionally excluded to prevent re-render loop
  }, [fetchData, toast]);

  // ── Sorting ────────────────────────────────────────────────────────────────
  const sortedTechPerformance = useMemo(() => {
    // DEBUG: log sort inputs so we can see when and why the memo fires
    debugLogger.current.debug('sortedTechPerformance: recalculating', {
      totalRows: techPerformance.length,
      sortKey:   sortConfig.key,
      direction: sortConfig.direction,
    });
    if (!sortConfig.key) return techPerformance;
    return [...techPerformance].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 :  1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ?  1 : -1;
      return 0;
    });
  }, [techPerformance, sortConfig]);

  // ── Filtering ──────────────────────────────────────────────────────────────
  const filteredTechPerformance = useMemo(() => {
    // DEBUG: log filter inputs so we can trace what criteria are active
    debugLogger.current.debug('filteredTechPerformance: recalculating', {
      inputRows:      sortedTechPerformance.length,
      searchTerm,
      selectedMetric,
      advancedFilters: {
        minCompletedWOs:   advancedFilters.minCompletedWOs,
        maxResolutionTime: advancedFilters.maxResolutionTime === Infinity ? 'Infinity' : advancedFilters.maxResolutionTime,
        minFixRate:        advancedFilters.minFixRate,
      },
    });
    const searchLower = searchTerm.toLowerCase();
    const result = sortedTechPerformance
      .filter(tech => {
        if (!tech.technician_name.toLowerCase().includes(searchLower)) return false;
        if (tech.completed_work_orders     < advancedFilters.minCompletedWOs)  return false;
        if (tech.avg_resolution_time_hours > advancedFilters.maxResolutionTime) return false;
        if (tech.first_time_fix_rate       < advancedFilters.minFixRate)        return false;
        if (selectedMetric === 'high-performers') {
          return tech.first_time_fix_rate > PERFORMANCE_THRESHOLDS.HIGH_FIX_RATE
            && tech.avg_resolution_time_hours < PERFORMANCE_THRESHOLDS.GOOD_RESOLUTION_HOURS;
        }
        if (selectedMetric === 'needs-improvement') {
          return tech.first_time_fix_rate < PERFORMANCE_THRESHOLDS.LOW_FIX_RATE
            || tech.avg_resolution_time_hours > PERFORMANCE_THRESHOLDS.POOR_RESOLUTION_HOURS;
        }
        return true;
      })
      .slice(0, selectedMetric === 'top-10' ? 10 : undefined);
    // DEBUG: log output row count after all filter + slice operations
    debugLogger.current.debug('filteredTechPerformance: result', {
      outputRows:  result.length,
      droppedRows: sortedTechPerformance.length - result.length,
    });
    return result;
  }, [sortedTechPerformance, searchTerm, selectedMetric, advancedFilters]);

  // ── Memoized display values ────────────────────────────────────────────────
  const freshnessMinutesAgo = useMemo(() => {
    if (!dataFreshness) return null;
    return Math.floor((Date.now() - new Date(dataFreshness).getTime()) / 60_000);
  }, [dataFreshness]);

  const freshnessTooltip = useMemo(() => {
    if (!dataFreshness) return '';
    return `Last updated: ${new Date(dataFreshness).toLocaleString()}`;
  }, [dataFreshness]);

  const mergedAiInsights = useMemo(() => {
    const fetched  = aiInsights      || [];
    const context  = contextInsights || [];
    const result   = fetched.length > 0 ? fetched : context;
    // DEBUG: log which source won and how many insights were resolved
    debugLogger.current.debug('mergedAiInsights: resolved', {
      source:        fetched.length > 0 ? 'API (aiInsights)' : 'context (AIInsightsContext)',
      fetchedCount:  fetched.length,
      contextCount:  context.length,
      resolvedCount: result.length,
    });
    return result;
  }, [aiInsights, contextInsights]);

  const effectiveAiHealthy = useMemo(() => {
    const result = isAiHealthy && wsIsConnected !== false;
    // DEBUG: log the inputs and combined result of the effective health flag
    debugLogger.current.debug('effectiveAiHealthy: computed', {
      isAiHealthy,
      wsIsConnected,
      result,
    });
    return result;
  }, [isAiHealthy, wsIsConnected]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleSort = useCallback((key) => {
    setSortConfig(prev => {
      const newDirection = prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc';
      // DEBUG: log the sort column and resolved direction
      debugLogger.current.debug('handleSort', {
        column:      key,
        previousKey: prev.key,
        previousDir: prev.direction,
        newDirection,
      });
      return { key, direction: newDirection };
    });
  }, []);

  const handleExportData = useCallback((format) => {
    const fmt = format ?? 'csv';
    debugLogger.current.info('Export initiated', { format: fmt });
    try {
      const exportPayload = {
        stats,
        techPerformance: filteredTechPerformance,
        funnelData,
        backlogData,
        byTypeData,
        byAssetTypeData,
        aiHealth,
        aiStats,
        aiInsights: mergedAiInsights,
        reviewQueue,
        metadata: {
          dateRange:   { start: startStr, end: endStr },
          exportDate:  new Date().toISOString(),
          cacheHitRate,
          apiCallCount,
        },
      };

      let content, filename, mimeType;

      if (fmt === 'json') {
        content  = JSON.stringify(exportPayload, null, 2);
        filename = `workorder-analytics-${Date.now()}.json`;
        mimeType = 'application/json';
      } else {
        const rows = [
          ['Technician', 'Completed WOs', 'Avg Resolution (hrs)', 'First-Time Fix Rate'].join(','),
          ...filteredTechPerformance.map(t => [
            t.technician_name,
            t.completed_work_orders,
            t.avg_resolution_time_hours.toFixed(2),
            (t.first_time_fix_rate * 100).toFixed(1),
          ].join(',')),
        ];
        content  = rows.join('\n');
        filename = `workorder-analytics-${Date.now()}.csv`;
        mimeType = 'text/csv';
      }

      const blob = new Blob([content], { type: mimeType });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);

      toast({
        title:       'Export Complete',
        description: `File: ${filename}`,
        status:      'success',
        duration:    3000,
        isClosable:  true,
      });
      debugLogger.current.info('Export completed', { filename });
    } catch (err) {
      debugLogger.current.error('Export failed', err);
      toast({
        title:       'Export Failed',
        description: err.message,
        status:      'error',
        duration:    4000,
        isClosable:  true,
      });
    }
  }, [
    stats, filteredTechPerformance, funnelData, backlogData,
    byTypeData, byAssetTypeData, aiHealth, aiStats, mergedAiInsights, reviewQueue,
    startStr, endStr, cacheHitRate, apiCallCount, toast,
  ]);

  const handleClearCache = useCallback(() => {
    // DEBUG: log the current cache size before clearing
    debugLogger.current.info('handleClearCache — clearing', {
      cacheSizeBefore: dataCache.current.size(),
    });
    dataCache.current.clear();
    debugLogger.current.info('Cache cleared');
    toast({
      title:       'Cache Cleared',
      description: 'All cached data has been removed',
      status:      'info',
      duration:    2000,
      isClosable:  true,
    });
  }, [toast]);

  // --- FIX 2: handleResetFilters had [toast] as its only dep, but toast is not
  // used inside the function. Meanwhile searchTerm, selectedMetric, and
  // advancedFilters were read for the debug log but absent from the dep array,
  // creating a stale closure — the log would always report the values from the
  // first render. Fixed by listing the three values actually referenced. ---
  const handleResetFilters = useCallback(() => {
    // DEBUG: log the filter values being reset from
    debugLogger.current.info('handleResetFilters — resetting from', {
      searchTerm,
      selectedMetric,
      advancedFilters,
    });
    setSearchTerm('');
    setSelectedMetric('all');
    setAdvancedFilters({ minCompletedWOs: 0, maxResolutionTime: Infinity, minFixRate: 0 });
    setShowAdvancedFilters(false);
    debugLogger.current.info('Filters reset');
  }, [searchTerm, selectedMetric, advancedFilters]);
  // --- END FIX 2 ---

  const handleTrainModel = useCallback(async () => {
    if (!featureFlags.canManageConfig) {
      // DEBUG: log the permission denial details
      debugLogger.current.warn('handleTrainModel — blocked: insufficient permissions', {
        canManageConfig: featureFlags.canManageConfig,
      });
      toast({ title: 'Insufficient permissions', status: 'warning', duration: 3000 });
      return;
    }
    try {
      setIsTraining(true);
      // DEBUG: log that training pipeline is being triggered
      debugLogger.current.info('handleTrainModel — triggering ML training pipeline');
      toast({
        title:       'Initializing Training Pipeline...',
        description: 'Connecting to ML Microservice.',
        status:      'info',
        duration:    3000,
        isClosable:  true,
      });

      await aiService.triggerPartPredictorTraining();

      toast({
        title:       'Training Started Successfully',
        description: 'The AI model is now analyzing the latest data in the background.',
        status:      'success',
        duration:    5000,
        isClosable:  true,
      });

      const freshModels = await aiService.getAvailableModels().catch(() => null);
      // DEBUG: log the freshModels response before processing
      debugLogger.current.debug('handleTrainModel — freshModels response', {
        received:   freshModels !== null,
        modelCount: freshModels?.models?.length ?? (Array.isArray(freshModels) ? freshModels.length : 0),
      });
      if (freshModels && mountedRef.current) {
        const arr = freshModels?.models || (Array.isArray(freshModels) ? freshModels : []);
        setAiModels(arr);
        dataCache.current.clear();
        // DEBUG: confirm model state update and cache invalidation post-training
        debugLogger.current.info('handleTrainModel — model registry updated and cache invalidated', {
          updatedModelCount: arr.length,
        });
      }
    } catch (err) {
      debugLogger.current.error('Train pipeline failed', err);
      toast({
        title:       'Training Pipeline Failed',
        description: err.message,
        status:      'error',
        duration:    6000,
        isClosable:  true,
      });
    } finally {
      if (mountedRef.current) setIsTraining(false);
      // DEBUG: log training pipeline completion (success or failure)
      debugLogger.current.debug('handleTrainModel — finally: isTraining -> false');
    }
  }, [featureFlags.canManageConfig, toast]);

  const handleCircuitChange = useCallback((newState) => {
    // DEBUG: log the incoming circuit state and the resulting health flag
    debugLogger.current.info('handleCircuitChange', {
      newState,
      isAiHealthyResult: newState === 'CLOSED',
    });
    if (newState === 'OPEN')   setIsAiHealthy(false);
    if (newState === 'CLOSED') setIsAiHealthy(true);
  }, []);

  // ── Loading state ─────────────────────────────────────────────────────────
  if (loading && !stats) {
    // DEBUG: log that the component is rendering the loading skeleton path
    debugLogger.current.debug('Rendering: loading skeleton — awaiting initial stats', {
      apiCallCount,
      hasStats: false,
    });
    return (
      <Box bg={bgColor} minH="60vh" p={6}>
        <Center h="60vh">
          <VStack spacing={4}>
            <Spinner size="xl" thickness="4px" speed="0.65s" color="blue.500" />
            <Text fontSize="lg" fontWeight="medium" color={textColor}>
              Loading FlowSense Analytics...
            </Text>
            <Progress size="xs" isIndeterminate w="300px" colorScheme="blue" />
            {debugMode && (
              <Code colorScheme="gray" fontSize="xs">
                Debug: {apiCallCount} API call(s) in-flight
              </Code>
            )}
          </VStack>
        </Center>
      </Box>
    );
  }

  // ── Error state ───────────────────────────────────────────────────────────
  if (error && !stats) {
    // DEBUG: log that the component is rendering the error path
    debugLogger.current.debug('Rendering: error state — no stats available', {
      error,
      debugMode,
    });
    return (
      <Box bg={bgColor} p={6}>
        <Alert
          status="error"
          variant="subtle"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          height="200px"
          borderRadius="lg"
        >
          <AlertIcon boxSize="40px" mr={0} />
          <Heading size="md" mt={4} mb={1} color={headingColor}>Data Loading Failed</Heading>
          <Text color={textColor}>{error}</Text>
          <HStack mt={4}>
            <Button colorScheme="red" onClick={() => fetchData(false)}>Retry</Button>
            {debugMode && (
              <Button variant="outline" onClick={onDebugOpen}>View Debug Logs</Button>
            )}
          </HStack>
        </Alert>
      </Box>
    );
  }

  // ── Main render ───────────────────────────────────────────────────────────
  return (
    <ErrorBoundary logger={debugLogger.current}>
      <Box bg={bgColor} minH="100vh" p={6}>
        <VStack spacing={8} align="stretch">

          {/* ================================================================
              HEADER
          ================================================================ */}
          <Box
            bg={headerBg}
            borderRadius="xl"
            boxShadow="xl"
            p={6}
            borderWidth="1px"
            borderColor={borderColor}
            position="relative"
            overflow="hidden"
          >
            <Box
              position="absolute" top={0} left={0} right={0} height="4px"
              bgGradient="linear(to-r, blue.400, purple.400, pink.400)"
              opacity={0.8}
            />

            <Flex justify="space-between" align="center" flexWrap="wrap" gap={4}>
              <VStack align="start" spacing={1}>
                <HStack spacing={3}>
                  <Box p={2} bg={iconBg} borderRadius="lg">
                    <Icon as={MdDashboard} boxSize={8} color="blue.500" />
                  </Box>
                  <VStack align="flex-start" spacing={0}>
                    <Heading as="h2" size="lg" color={headingColor}>
                      FlowSense Analytics Hub
                    </Heading>
                    <HStack spacing={2} mt={1}>
                      <Badge colorScheme="blue" fontSize="xs">v3.3 Enterprise</Badge>
                      <Badge
                        colorScheme={effectiveAiHealthy ? 'green' : 'orange'}
                        fontSize="xs"
                      >
                        <HStack spacing={1}>
                          <Icon as={MdPsychology} boxSize={3} />
                          <Text>AI: {effectiveAiHealthy ? 'Active' : 'Degraded'}</Text>
                        </HStack>
                      </Badge>
                      {dataFreshness && (
                        <Tooltip label={freshnessTooltip} hasArrow>
                          <Badge colorScheme="green" fontSize="xs" cursor="help">
                            {freshnessMinutesAgo}m ago
                          </Badge>
                        </Tooltip>
                      )}
                    </HStack>
                  </VStack>
                </HStack>
                {debugMode && dataFreshness && (
                  <Text fontSize="xs" color={mutedColor} mt={2}>
                    Cache hits: {cacheHitRate} &bull; API calls: {apiCallCount}
                    {performanceMetrics.lastFetchDuration &&
                      ` • Last fetch: ${performanceMetrics.lastFetchDuration.toFixed(0)}ms`}
                  </Text>
                )}
              </VStack>

              <HStack spacing={2} flexWrap="wrap">
                <Tooltip label={`WebSocket: ${connectionStatus}`}>
                  <Badge
                    colorScheme={
                      connectionStatus === 'connected' ? 'green' :
                      connectionStatus === 'degraded'  ? 'orange' : 'red'
                    }
                    fontSize="sm" px={3} py={1}
                  >
                    {connectionStatus}
                  </Badge>
                </Tooltip>

                {autoRefresh && (
                  <Badge colorScheme="blue" fontSize="sm" px={3} py={1}>
                    Auto {refreshInterval / 1000}s
                  </Badge>
                )}

                <Button
                  leftIcon={<Icon as={MdRefresh} />}
                  size="sm"
                  onClick={() => fetchData(false)}
                  isLoading={loading}
                  variant="outline"
                  colorScheme="blue"
                >
                  Refresh
                </Button>

                <Menu>
                  <MenuButton as={Button} leftIcon={<Icon as={MdDownload} />} size="sm" colorScheme="blue">
                    Export
                  </MenuButton>
                  <MenuList>
                    <MenuItem onClick={() => handleExportData('csv')}>Export as CSV</MenuItem>
                    <MenuItem onClick={() => handleExportData('json')}>Export as JSON</MenuItem>
                  </MenuList>
                </Menu>

                <Button
                  leftIcon={<Icon as={MdSettings} />}
                  size="sm"
                  onClick={onSettingsOpen}
                  variant="outline"
                >
                  Settings
                </Button>

                {debugMode && (
                  <Button
                    leftIcon={<Icon as={MdBugReport} />}
                    size="sm"
                    onClick={onDebugOpen}
                    colorScheme="orange"
                    variant="outline"
                  >
                    Debug
                  </Button>
                )}
              </HStack>
            </Flex>
          </Box>

          {/* ================================================================
              PERFORMANCE BANNER (debug only)
          ================================================================ */}
          {debugMode && performanceMetrics.lastFetchDuration && (
            <Alert status="info" variant="left-accent" borderRadius="lg">
              <AlertIcon as={MdSpeed} />
              <Box flex="1">
                <Text fontWeight="bold" color={textColor}>Performance Metrics</Text>
                <HStack spacing={4} fontSize="sm" color={mutedColor}>
                  <Text>Last fetch: {performanceMetrics.lastFetchDuration.toFixed(2)}ms</Text>
                  <Text>Avg fetch: {performanceMetrics.avgFetchDuration?.toFixed(2)}ms</Text>
                  <Text>Cache size: {dataCache.current.size()}</Text>
                </HStack>
              </Box>
            </Alert>
          )}

          {/* ================================================================
              LIVE EVENT TICKER
          ================================================================ */}
          {liveEventFeed && liveEventFeed.length > 0 && (
            <Box
              bg={liveTickerBg}
              borderRadius="lg"
              borderWidth="1px"
              borderColor={liveTickerBorder}
              px={4}
              py={2}
              overflow="hidden"
            >
              <HStack spacing={3} overflowX="auto" pb={1} className="custom-scrollbar">
                <HStack spacing={1} flexShrink={0}>
                  <Icon as={MdNotifications} color="blue.400" boxSize={4} />
                  <Text
                    fontSize="xs"
                    fontWeight="700"
                    color={mutedColor}
                    textTransform="uppercase"
                    letterSpacing="wider"
                  >
                    Live
                  </Text>
                </HStack>
                {liveEventFeed.slice(0, 5).map((event, i) => (
                  <Badge
                    key={i}
                    colorScheme={
                      event.type === 'circuit_open'        ? 'red'    :
                      event.type === 'sla_watchdog_update' ? 'orange' :
                      event.type === 'scan_complete'       ? 'green'  : 'blue'
                    }
                    fontSize="2xs"
                    px={2}
                    py={1}
                    borderRadius="full"
                    flexShrink={0}
                  >
                    {event.type || event.channel}
                  </Badge>
                ))}
              </HStack>
            </Box>
          )}

          {/* ================================================================
              TABS
          ================================================================ */}
          <Tabs
            variant="enclosed"
            colorScheme="blue"
            index={activeTab}
            onChange={setActiveTab}
            isLazy
          >
            <TabList mb={4} borderBottomWidth="2px">
              <Tab
                fontWeight="bold"
                _selected={{ color: 'blue.500', borderBottomColor: 'blue.500' }}
              >
                <Icon as={MdDashboard} mr={2} />Operational Overview
              </Tab>
              <Tab
                fontWeight="bold"
                _selected={{ color: 'purple.500', borderBottomColor: 'purple.500' }}
              >
                <Icon as={MdPsychology} mr={2} />AI & Learning Insights
                <Badge ml={2} colorScheme="purple" variant="solid" fontSize="xs">ACTIVE</Badge>
              </Tab>
            </TabList>

            <TabPanels>

              {/* ==============================================================
                  TAB 1: OPERATIONAL OVERVIEW
              ============================================================== */}
              <TabPanel p={0}>
                <VStack spacing={8} align="stretch">

                  <Box
                    bg={cardBg}
                    p={5}
                    borderRadius="xl"
                    borderWidth="1px"
                    borderColor={borderColor}
                    boxShadow="md"
                  >
                    <WorkOrderLiveMetrics onCircuitChange={handleCircuitChange} />
                  </Box>

                  <WorkOrderStatsWidget
                    autoRefresh={autoRefresh}
                    refreshInterval={refreshInterval}
                    enableDebug={debugMode}
                    showTrends
                    isWsConnected={connectionStatus === 'connected'} 
                    refreshTrigger={apiCallCount}
                  />

                  <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={8}>
                    <Box
                      bg={cardBg}
                      borderRadius="xl"
                      boxShadow="md"
                      borderWidth="1px"
                      borderColor={borderColor}
                    >
                      <WorkOrderByPriorityChart
                        data={byTypeData}
                        isLoading={loading}
                        enableDebug={debugMode}
                        enableExport
                      />
                    </Box>

                    <Box
                      bg={cardBg}
                      p={5}
                      boxShadow="md"
                      borderWidth="1px"
                      borderRadius="xl"
                      borderColor={borderColor}
                    >
                     <WorkOrderBacklogAging 
    data={backlogData} 
    isLoading={loading} 
    enableDebug={debugMode} 
  />
</Box>

                    <Box
                      bg={cardBg}
                      p={5}
                      boxShadow="md"
                      borderWidth="1px"
                      borderRadius="xl"
                      gridColumn={{ base: 1, lg: 'span 2' }}
                      borderColor={borderColor}
                    >
                      <WorkOrderAssetTypeChart 
    data={byAssetTypeData} 
    isLoading={loading} 
    enableDebug={debugMode} 
  />
</Box>

                    {/* --- FIX 3: Guard WorkOrderFlowDiagram with a Skeleton during
                    initial load so the DOM is fully laid out before Recharts'
                    ResponsiveContainer attempts to measure its container. This
                    prevents the "width(-1) and height(-1) should be greater than 0"
                    console error caused by measuring a not-yet-painted container. --- */}
                    <Box
                      bg={cardBg}
                      p={5}
                      boxShadow="md"
                      borderWidth="1px"
                      borderRadius="xl"
                      gridColumn={{ base: 1, lg: 'span 2' }}
                      borderColor={borderColor}
                    >
                      {loading ? (
                        <Skeleton height="300px" borderRadius="xl" />
                      ) : (
                        <WorkOrderFlowDiagram />
                      )}
                    </Box>
                    {/* --- END FIX 3 --- */}

                  </SimpleGrid>

                  {/* ── TECHNICIAN PERFORMANCE ─────────────────────────────── */}
                  <Box
                    bg={cardBg}
                    p={5}
                    boxShadow="md"
                    borderWidth="1px"
                    borderRadius="xl"
                    borderColor={borderColor}
                  >
                    <TechnicianPerformanceWidget 
  data={techPerformance} 
  isLoading={loading} 
/>
                  </Box>

                </VStack>
              </TabPanel>

              {/* ==============================================================
                  TAB 2: AI & LEARNING INSIGHTS
              ============================================================== */}
              <TabPanel p={0}>
                <VStack spacing={8} align="stretch">

                  <Box
                    bg={aiHealthBg}
                    p={5}
                    borderRadius="xl"
                    borderWidth="1px"
                    borderColor={borderColor}
                    boxShadow="md"
                  >
                    <Flex justify="space-between" align="center" flexWrap="wrap" gap={4}>
                      <HStack spacing={4}>
                        <Box p={3} bg={cardBg} borderRadius="lg">
                          <Icon as={MdSmartToy} boxSize={8} color="purple.500" />
                        </Box>
                        <Box>
                          <Heading size="md" color={headingColor}>AI System Status</Heading>
                          <HStack spacing={2} mt={1}>
                            <Badge
                              colorScheme={effectiveAiHealthy ? 'green' : 'orange'}
                              fontSize="sm"
                            >
                              {aiHealth?.status?.toUpperCase() || 'UNKNOWN'}
                            </Badge>
                            {aiHealth?.last_check && (
                              <Text fontSize="xs" color={mutedColor}>
                                Last check: {new Date(aiHealth.last_check).toLocaleTimeString()}
                              </Text>
                            )}
                            {contextWatchdog?.efficiency_rating != null && (
                              <Badge colorScheme="teal" fontSize="xs">
                                WD Efficiency: {contextWatchdog.efficiency_rating}%
                              </Badge>
                            )}
                          </HStack>
                        </Box>
                      </HStack>

                      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                        <Stat size="sm" bg={cardBg} p={3} borderRadius="md">
                          <StatLabel fontSize="xs">Active Models</StatLabel>
                          <StatNumber fontSize="2xl" color="green.500">
                            {aiStats?.activeModels || 0}
                          </StatNumber>
                        </Stat>
                        <Stat size="sm" bg={cardBg} p={3} borderRadius="md">
                          <StatLabel fontSize="xs">Staging Models</StatLabel>
                          <StatNumber fontSize="2xl" color="orange.500">
                            {aiStats?.stagingModels || 0}
                          </StatNumber>
                        </Stat>
                        <Stat size="sm" bg={cardBg} p={3} borderRadius="md">
                          <StatLabel fontSize="xs">Total Models</StatLabel>
                          <StatNumber fontSize="2xl" color="blue.500">
                            {aiStats?.totalModels || 0}
                          </StatNumber>
                        </Stat>
                      </SimpleGrid>
                    </Flex>
                  </Box>

                  <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={8} alignItems="start">

                    <Box><NlpPartPredictorWidget dateRange={dateRange} /></Box>

                    <VStack spacing={8} align="stretch">

                      <Box
                        bg={cardBg}
                        p={6}
                        borderRadius="xl"
                        borderWidth="1px"
                        borderColor={borderColor}
                        boxShadow="md"
                      >
                        <Flex justify="space-between" align="center" mb={4}>
                          <HStack>
                            <Icon as={MdModelTraining} boxSize={6} color="purple.500" />
                            <Heading size="md" color={headingColor}>
                              Predictive Model Registry
                            </Heading>
                          </HStack>
                          <Badge colorScheme="blue" fontSize="sm">RUL_PREDICTION</Badge>
                        </Flex>

                        <TableContainer>
                          <Table variant="simple" size="sm">
                            <Thead bg={statBg}>
                              <Tr>
                                <Th>Model Version</Th>
                                <Th>Status</Th>
                                <Th isNumeric>Performance</Th>
                                <Th>Trained Date</Th>
                              </Tr>
                            </Thead>
                            <Tbody>
                              {aiModels.slice(0, 5).map(model => (
                                <Tr key={model.id} _hover={{ bg: tableRowHoverBg }}>
                                  <Td>
                                    <Code fontSize="xs" colorScheme="purple">
                                      {model.model_version}
                                    </Code>
                                  </Td>
                                  <Td>
                                    <Badge
                                      colorScheme={
                                        model.status === 'production' ? 'green'  :
                                        model.status === 'staging'    ? 'blue'   : 'gray'
                                      }
                                    >
                                      {model.status}
                                    </Badge>
                                  </Td>
                                  <Td isNumeric>
                                    {model.performance_metric_value ? (
                                      <Badge colorScheme="teal">
                                        MAE: {Number(model.performance_metric_value).toFixed(2)}
                                      </Badge>
                                    ) : (
                                      <Text fontSize="xs" color={mutedColor}>N/A</Text>
                                    )}
                                  </Td>
                                  <Td fontSize="xs" color={mutedColor}>
                                    {new Date(
                                      model.last_trained_at || model.created_at
                                    ).toLocaleDateString()}
                                  </Td>
                                </Tr>
                              ))}
                              {aiModels.length === 0 && (
                                <Tr>
                                  <Td colSpan={4} textAlign="center" py={8}>
                                    <VStack>
                                      <Icon as={MdWarning} boxSize={10} color="gray.400" />
                                      <Text color={mutedColor}>No models found</Text>
                                    </VStack>
                                  </Td>
                                </Tr>
                              )}
                            </Tbody>
                          </Table>
                        </TableContainer>

                        <HStack justify="flex-end" mt={4} spacing={2}>
                          <Button
                            size="sm"
                            leftIcon={<Icon as={MdModelTraining} />}
                            variant="outline"
                            colorScheme="purple"
                            onClick={handleTrainModel}
                            isLoading={isTraining}
                            isDisabled={!featureFlags.canManageConfig}
                            title={
                              !featureFlags.canManageConfig
                                ? 'Admin access required'
                                : undefined
                            }
                          >
                            Train New Model
                          </Button>
                          <Button
                            size="sm"
                            leftIcon={<Icon as={MdRefresh} />}
                            variant="ghost"
                            onClick={() => fetchData(false)}
                          >
                            Refresh
                          </Button>
                        </HStack>
                      </Box>

                      <HumanVerificationWidget
                        reviewQueue={reviewQueue}
                        verifiedCount={verifiedCount}
                        onReviewClick={() => setIsReviewModalOpen(true)}
                        isLoading={loading}
                      />

                    </VStack>
                  </SimpleGrid>

                  {mergedAiInsights.length > 0 && (
                    <Box
                      bg={cardBg}
                      p={5}
                      borderRadius="xl"
                      borderWidth="1px"
                      borderColor={borderColor}
                      boxShadow="md"
                    >
                      <HStack mb={4}>
                        <Icon as={MdLightbulb} boxSize={6} color="yellow.500" />
                        <Heading size="md" color={headingColor}>
                          AI-Powered Recommendations
                        </Heading>
                        <Badge
                          colorScheme={aiInsights.length > 0 ? 'green' : 'blue'}
                          fontSize="2xs"
                          variant="subtle"
                        >
                          {aiInsights.length > 0 ? 'API' : 'Live Context'}
                        </Badge>
                      </HStack>
                      <VStack align="stretch" spacing={3}>
                        {mergedAiInsights.map((insight, index) => (
                          <Alert
                            key={index}
                            status={insight.type || 'info'}
                            variant="left-accent"
                            borderRadius="md"
                          >
                            <AlertIcon />
                            <Box flex="1">
                              <Text fontWeight="bold" fontSize="sm">
                                {insight.title || 'System Insight'}
                              </Text>
                              <Text fontSize="sm">{insight.message}</Text>
                            </Box>
                          </Alert>
                        ))}
                      </VStack>
                    </Box>
                  )}

                </VStack>
              </TabPanel>

            </TabPanels>
          </Tabs>

          {/* ================================================================
              SETTINGS MODAL
          ================================================================ */}
          <Modal isOpen={isSettingsOpen} onClose={onSettingsClose} size="xl">
            <ModalOverlay />
            <ModalContent bg={cardBg}>
              <ModalHeader color={headingColor}>Dashboard Settings</ModalHeader>
              <ModalCloseButton />
              <ModalBody pb={6}>
                <VStack spacing={4} align="stretch">
                  <FormControl display="flex" alignItems="center">
                    <FormLabel mb="0" color={textColor}>Debug Mode</FormLabel>
                    <Switch
                      isChecked={debugMode}
                      onChange={e => setDebugMode(e.target.checked)}
                    />
                  </FormControl>

                  <FormControl display="flex" alignItems="center">
                    <FormLabel mb="0" color={textColor}>Auto-Refresh</FormLabel>
                    <Switch
                      isChecked={autoRefresh}
                      onChange={e => setAutoRefresh(e.target.checked)}
                    />
                  </FormControl>

                  {autoRefresh && (
                    <FormControl>
                      <FormLabel color={textColor}>Refresh Interval (seconds)</FormLabel>
                      <Select
                        value={refreshInterval}
                        onChange={e => setRefreshInterval(Number(e.target.value))}
                        bg={statBg}
                      >
                        {REFRESH_INTERVAL_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  <Divider />

                  <FormControl>
                    <FormLabel color={textColor}>Advanced Filters</FormLabel>
                    <VStack spacing={2} align="stretch">
                      <HStack>
                        <Text fontSize="sm" w="200px" color={textColor}>Min Completed WOs:</Text>
                        <Input
                          type="number"
                          size="sm"
                          value={advancedFilters.minCompletedWOs}
                          onChange={e => setAdvancedFilters(prev => ({
                            ...prev, minCompletedWOs: Number(e.target.value),
                          }))}
                          bg={statBg}
                        />
                      </HStack>
                      <HStack>
                        <Text fontSize="sm" w="200px" color={textColor}>Max Resolution Time (hrs):</Text>
                        <Input
                          type="number"
                          size="sm"
                          value={
                            advancedFilters.maxResolutionTime === Infinity
                              ? ''
                              : advancedFilters.maxResolutionTime
                          }
                          onChange={e => setAdvancedFilters(prev => ({
                            ...prev,
                            maxResolutionTime: e.target.value === '' ? Infinity : Number(e.target.value),
                          }))}
                          placeholder="No limit"
                          bg={statBg}
                        />
                      </HStack>
                      <HStack>
                        <Text fontSize="sm" w="200px" color={textColor}>Min Fix Rate (0-1):</Text>
                        <Input
                          type="number"
                          size="sm"
                          step="0.1"
                          min="0"
                          max="1"
                          value={advancedFilters.minFixRate}
                          onChange={e => setAdvancedFilters(prev => ({
                            ...prev, minFixRate: Number(e.target.value),
                          }))}
                          bg={statBg}
                        />
                      </HStack>
                    </VStack>
                  </FormControl>

                  <Divider />

                  <HStack justify="space-between">
                    <Button size="sm" onClick={handleClearCache} colorScheme="orange">
                      Clear Cache
                    </Button>
                    <Text fontSize="xs" color={mutedColor}>
                      Cache size: {dataCache.current.size()} items
                    </Text>
                  </HStack>
                </VStack>
              </ModalBody>
            </ModalContent>
          </Modal>

          {/* ================================================================
              DEBUG MODAL
          ================================================================ */}
          <Modal isOpen={isDebugOpen} onClose={onDebugClose} size="6xl">
            <ModalOverlay />
            <ModalContent maxH="80vh" bg={cardBg}>
              <ModalHeader color={headingColor}>Debug Console</ModalHeader>
              <ModalCloseButton />
              <ModalBody pb={6} overflowY="auto">
                <VStack spacing={4} align="stretch">
                  <HStack>
                    <Button
                      size="sm"
                      onClick={() => {
                        const logs = debugLogger.current.exportLogs();
                        const blob = new Blob([logs], { type: 'application/json' });
                        const url  = URL.createObjectURL(blob);
                        const a    = document.createElement('a');
                        a.href = url;
                        a.download = `debug-logs-${Date.now()}.json`;
                        a.click();
                        URL.revokeObjectURL(url);
                      }}
                    >
                      Export Logs
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => debugLogger.current.clearLogs()}
                    >
                      Clear Logs
                    </Button>
                  </HStack>

                  <Accordion allowMultiple>
                    {[
                      { level: 'info',  color: 'gray'   },
                      { level: 'error', color: 'red'    },
                      { level: 'warn',  color: 'yellow' },
                    ].map(({ level, color }) => (
                      <AccordionItem key={level}>
                        <h2>
                          <AccordionButton>
                            <Box flex="1" textAlign="left" color={textColor}>
                              {level.charAt(0).toUpperCase() + level.slice(1)} Logs (
                              {debugLogger.current.getLogs(level).length})
                            </Box>
                            <AccordionIcon />
                          </AccordionButton>
                        </h2>
                        <AccordionPanel pb={4}>
                          <VStack
                            align="stretch"
                            spacing={2}
                            maxH="300px"
                            overflowY="auto"
                          >
                            {debugLogger.current.getLogs(level).map((log, i) => (
                              <Code
                                key={i}
                                fontSize="xs"
                                colorScheme={color}
                                p={2}
                              >
                                [{log.timestamp}] {log.message}
                                {log.data && (
                                  <pre>{JSON.stringify(log.data, null, 2)}</pre>
                                )}
                              </Code>
                            ))}
                          </VStack>
                        </AccordionPanel>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </VStack>
              </ModalBody>
            </ModalContent>
          </Modal>

          {/* ================================================================
              AI REVIEW QUEUE MODAL
          ================================================================ */}
          <AIReviewQueueModal
            isOpen={isReviewModalOpen}
            onClose={() => setIsReviewModalOpen(false)}
            queue={reviewQueue}
            onQueueEmpty={() => fetchData(false)}
          />

        </VStack>
      </Box>
    </ErrorBoundary>
  );
}

export default WorkOrderDeepDiveDashboard;